/*

  RiddleScore.ts

  Score display component for Gáta Dagsins

  Copyright (C) 2025 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { Component, m } from "./mithril";
import { IRiddle } from "./types";

interface IRiddleScoreAttributes {
  riddle: IRiddle;
  mode?: "desktop" | "mobile"; // Display mode: desktop (default) or mobile
}

export const RiddleScore: Component<IRiddleScoreAttributes> = {
  view: (vnode) => {
    const { riddle, mode = "desktop" } = vnode.attrs;
    if (!riddle) return m("div");

    const score = riddle.currentScore;
    const hasValidMove = score !== undefined;
    const hasTiles = riddle.tilesPlaced().length > 0;

    const baseClass = (
      mode === "mobile" ? ".mobile-score" : ".gata-dagsins-score"
    );
    let classes = [baseClass];
    let displayText = "0";

    if (!hasTiles) {
      // State 1: No tiles on board - grayed/disabled, showing zero
      classes.push(".disabled");
      displayText = "0";
    } else if (!hasValidMove) {
      // State 2: Illegal configuration - error state with question mark
      classes.push(".illegal");
      displayText = "?";
    } else if (!riddle.wordGood) {
      // State 3: Word correctly laid out but not found in the dictionary
      classes.push(".word-bad");
      displayText = score.toString();
    } else {
      // States 3-5: Valid word with score classification (cold, warm, hot)
      displayText = score.toString();
      if (score < riddle.warmBoundary) {
        classes.push(".cold");
      } else if (score < riddle.hotBoundary) {
        classes.push(".warm");
      } else {
        classes.push(".hot");
      }
      // Add celebration class if the player achieved the best possible score
      if (score >= riddle.bestPossibleScore) {
        classes.push(".celebrate");
      }
    }

    const legendClass = mode === "mobile" ? ".mobile-score-legend" : ".gata-dagsins-legend";
    return m("div" + classes.join(""),
      m("span" + legendClass, displayText)
    );
  }
};
